import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import { TableSkeleton } from '../common/SkeletonLoaders';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridRowSelectionModel,
  GridPaginationModel,
} from '@mui/x-data-grid';
import {
  Download as DownloadIcon,
  FolderOpen as FolderOpenIcon,
} from '@mui/icons-material';
import { themeColors } from '../../utils/theme';
import { getFileIcon } from '../../utils/fileIconMap';
import { useS3Service } from '../../hooks/useS3Service';
import { useS3Browser } from '../../contexts/S3BrowserContext';
import type { S3Item, S3Object, S3ItemWithId } from '../../types/s3';
import { S3ServiceError } from '../../services/S3Service';
import S3DownloadManager from './S3DownloadManager';
import S3PaginationControls from './S3PaginationControls';
import S3SelectionControls from './S3SelectionControls';
import BulkDownloadButton from './BulkDownloadButton';
import S3UploadButton from './S3UploadButton';
import S3UploadProgress from './S3UploadProgress';
import toast from 'react-hot-toast';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useS3Upload } from '../../hooks/useS3Upload';

/**
 * S3FileTable Component
 * 
 * File browser table for AWS S3 buckets with stable React keys and optimized rendering.
 * 
 * **NOTE: Checkbox selection is currently disabled.** The underlying selection state 
 * management remains in S3BrowserContext for future re-enablement.
 * 
 * Features:
 * - Navigation through folder hierarchy
 * - Single file downloads
 * - Bulk download as ZIP (temporarily disabled with checkbox removal)
 * - Progress tracking for downloads
 * - Error handling and retry mechanisms
 * - Immediate updates when filter state changes
 * - Optimized re-rendering with useMemo and useCallback
 * 
 * **Stable Key Pattern:**
 * This component ensures stable React keys by using `item.key` (the S3 object key/path)
 * as the unique identifier for all items. This prevents:
 * - Duplicate key warnings in the console
 * - Unnecessary DOM mutations during re-filtering
 * - Lost selection state during filter updates
 * - Stale items persisting in the rendered list
 * 
 * @see docs/feature-plans/checkbox-selection-removal-plan.md
 * @component
 * @returns {React.FC} The S3 file table component
 */
const S3FileTable: React.FC = () => {
  // S3 service hook for data fetching
  const { s3Service, isAuthenticated } = useS3Service();

  // Get S3 browser state and actions from context
  const { 
    state, 
    navigateTo, 
    selectItems,
    // Controlled mode props for filter integration
    filteredReports,
    isFilterLoading,
    filterError,
    paginationMetadata,
    onPageChange,
    onPageSizeChange,
  } = useS3Browser();

  // Get upload context for accessing upload jobs
  const { state: uploadState } = useS3Upload();

  // Determine if we're in controlled mode (filter integration)
  const isControlledMode = filteredReports !== undefined;

  // Get current path from context
  const currentPath = state.navigation.currentPath;

  // Component state (local for data management)
  // Use local pagination for standalone mode, controlled for filter mode
  const [localPaginationModel, setLocalPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 100, // Maximum allowed by MUI DataGrid MIT version
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  // Determine effective pagination model based on mode
  const paginationModel = isControlledMode && paginationMetadata
    ? {
        page: paginationMetadata.currentPage,
        pageSize: paginationMetadata.pageSize,
      }
    : localPaginationModel;

  // React Query for infinite loading (only enabled in standalone mode)
  const { 
    data, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage, 
    isLoading, 
    isError, 
    error 
  } = useInfiniteQuery({
    queryKey: ['s3Items', currentPath, localPaginationModel.pageSize],
    queryFn: async ({ pageParam = undefined }: { pageParam?: string }) => {
      if (!s3Service || !isAuthenticated) {
        throw new Error('S3 Service not initialized or user not authenticated');
      }
      const result = await s3Service.listObjects(
        currentPath,
        '/',
        localPaginationModel.pageSize,
        pageParam
      );
      return result;
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextContinuationToken,
    enabled: !!s3Service && isAuthenticated && !isControlledMode, // Disable in controlled mode
  });

  // Determine items and loading state based on mode
  const rawItems = isControlledMode 
    ? (filteredReports || [])
    : (data?.pages.flatMap(page => page.items) || []);
  
  /**
   * Use raw items directly - search filtering now handled by S3 prefix
   */
  const items = rawItems;
  
  const loading = isControlledMode 
    ? (isFilterLoading || false)
    : (isLoading || isFetchingNextPage);
  
  const currentError = isControlledMode 
    ? filterError
    : (isError ? (error?.message || 'Failed to load files') : null);

  /**
   * Handle pagination model changes from DataGrid
   * In controlled mode, calls parent callbacks
   * In standalone mode, updates local state
   */
  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    if (isControlledMode) {
      // In controlled mode, notify parent of changes
      if (newModel.page !== paginationModel.page && onPageChange) {
        onPageChange(newModel.page);
      }
      if (newModel.pageSize !== paginationModel.pageSize && onPageSizeChange) {
        onPageSizeChange(newModel.pageSize);
      }
    } else {
      // In standalone mode, update local state
      setLocalPaginationModel(newModel);
    }
  }, [isControlledMode, paginationModel.page, paginationModel.pageSize, onPageChange, onPageSizeChange]);

  /**
   * Handle scroll event to detect when to load more rows
   * @param {React.UIEvent<HTMLDivElement>} event - The scroll event
   */
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    // Check if scrolled to bottom
    if (target.scrollHeight - target.scrollTop === target.clientHeight) {
      if (!isFetchingNextPage && hasNextPage) {
        fetchNextPage();
      }
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  // Download state
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'downloading' | 'compressing' | 'success' | 'error'>('idle');
  const [downloadMessage, setDownloadMessage] = useState<string>('');
  const [lastDownloadItem, setLastDownloadItem] = useState<S3Object | S3Object[] | null>(null);

  // TODO: FEATURE-CHECKBOX-SELECTION [REMOVED]
  // Description: Selection model creation from selected items
  // Restore: Uncomment the code below to restore checkbox selection
  // Ref: docs/feature-plans/checkbox-selection-removal-plan.md
  /**
   * Create selection model from context state using stable item keys.
   * 
   * Maps selected items to their keys (S3 object paths) which are used by DataGrid
   * for tracking checkbox selection state. Using stable keys ensures:
   * - Selection state persists across filter changes (when same items remain)
   * - No flickering or lost selections during re-renders
   * - Consistent selection behavior
   */
  // const selectionModel = state.selection.selectedItems.map(item => item.key);
  
  // Empty selection model since checkbox selection is disabled
  const selectionModel: string[] = [];

  /**
   * Log current path for debugging upload issues
   */
  useEffect(() => {
    console.log('[S3FileTable] Current path changed:', currentPath);
  }, [currentPath]);

  // TODO: FEATURE-CHECKBOX-SELECTION [REMOVED]
  // Description: DataGrid selection change handler
  // Restore: Uncomment the code below to restore selection handling
  // Ref: docs/feature-plans/checkbox-selection-removal-plan.md
  /**
   * Handle selection changes from DataGrid.
   * 
   * Converts DataGrid's key-based selection model back to full S3Item objects
   * and updates the context state. Uses stable item.key for matching, ensuring
   * correct item identification even after filtering or sorting operations.
   * 
   * @param {GridRowSelectionModel} newSelectionModel - Array of selected item keys from DataGrid
   */
  // const handleSelectionChange = useCallback((newSelectionModel: GridRowSelectionModel) => {
  //   const selectedItems = items.filter(item => 
  //     newSelectionModel.includes(item.key)
  //   );
  //   selectItems(selectedItems);
  // }, [items, selectItems]);

  /**
   * Handle folder navigation on row double-click
   * @param {any} params - DataGrid row parameters
   */
  const handleRowDoubleClick = (params: any) => {
    const item = params.row as S3Item;
    if (item.type === 'folder') {
      navigateTo(item.key);
    }
  };

  /**
   * Handle single file download
   * @param {S3Object} item - The S3 object to download
   * @returns {Promise<void>} Promise that resolves when download completes
   */
  const handleDownload = async (item: S3Object) => {
    if (!s3Service) return;

    setDownloadStatus('downloading');
    setDownloadMessage(`Downloading ${item.name}...`);
    setDownloadProgress(0);

    try {
      const blob = await s3Service.downloadObject(item.key);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setDownloadStatus('success');
      setDownloadMessage(`Successfully downloaded ${item.name}`);
      toast.success(`Successfully downloaded ${item.name}`);
    } catch (err) {
      console.error('Error downloading file:', err);
      setDownloadStatus('error');
      const errorMessage = err instanceof S3ServiceError ? err.userMessage : `Failed to download ${item.name}: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setDownloadMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      // Reset download status after a short delay for success/error messages to be visible
      setTimeout(() => setDownloadStatus('idle'), 3000);
    }
  };

  // TODO: FEATURE-CHECKBOX-SELECTION [REMOVED]
  // Description: Bulk download handler - preserved for future re-enablement
  // Restore: This function remains available but is not triggered until checkbox selection is restored
  // Ref: docs/feature-plans/checkbox-selection-removal-plan.md
  /**
   * Handle bulk download of selected files as ZIP archive
   * Uses server-side ZIP generation via performServerBulkDownload
   * 
   * Task 7.3: Implement client-side bulk download trigger and file saving
   * @returns {Promise<void>} Promise that resolves when bulk download completes
   */
  const handleBulkDownload = async () => {
    if (!s3Service || state.selection.selectedFiles.length === 0) return;

    setLastDownloadItem(state.selection.selectedFiles);
    setDownloadStatus('downloading');
    setDownloadMessage(`Downloading ${state.selection.selectedFiles.length} files...`);
    setDownloadProgress(0);

    try {
      // Import performServerBulkDownload from the server adapter
      const { performServerBulkDownload, getServerDownloadErrorMessage } = await import('../../services/bulkDownloadServerAdapter');
      
      // Call server-side bulk download
      const result = await performServerBulkDownload(state.selection.selectedFiles, {
        zipFileName: `reports-${new Date().toISOString().slice(0, 10)}`,
        onProgress: (bytes, total) => {
          const progress = Math.min(100, Math.round((bytes / total) * 100));
          setDownloadProgress(progress);
          setDownloadMessage(`Downloading: ${progress}% complete`);
        },
      });

      if (result.success) {
        setDownloadStatus('success');
        const successMessage = `Successfully downloaded ${state.selection.selectedFiles.length} files as ZIP`;
        setDownloadMessage(successMessage);
        toast.success(successMessage);
        selectItems([]); // Clear selection after successful download
      } else {
        // Handle error from server
        const errorMessage = result.errorType 
          ? getServerDownloadErrorMessage(result.errorType)
          : (result.error || 'Download failed');
        
        setDownloadStatus('error');
        setDownloadMessage(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error('Error during server bulk download:', err);
      setDownloadStatus('error');
      const errorMessage = `Failed to download files: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setDownloadMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setTimeout(() => setDownloadStatus('idle'), 3000);
    }
  };

  /**
   * Handle retry of last failed download
   * @returns {void}
   */
  const handleRetry = useCallback(() => {
    if (lastDownloadItem) {
      if (Array.isArray(lastDownloadItem)) {
        handleBulkDownload();
      } else {
        handleDownload(lastDownloadItem);
      }
    }
  }, [lastDownloadItem]);

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size string
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  /**
   * Format date for display
   * @param {Date} date - Date to format
   * @returns {string} Formatted date string
   */
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Column definitions for DataGrid
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => {
        const item = params.row as S3Item;
        const isFolder = item.type === 'folder';
        const isSelected = selectionModel.includes(item.key);
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
              {getFileIcon(item.name, isFolder)}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: isSelected 
                  ? themeColors.primary 
                  : (isFolder ? themeColors.primary : themeColors.text),
                fontWeight: isSelected ? 600 : (isFolder ? 500 : 400),
                cursor: isFolder ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
              }}
            >
              {item.name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'size',
      headerName: 'Size',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const item = params.row as S3Item;
        const isSelected = selectionModel.includes(item.key);
        
        if (item.type === 'folder') {
          return (
            <Chip
              label="Folder"
              size="small"
              sx={{
                bgcolor: isSelected ? themeColors.primary : themeColors.accent,
                color: isSelected ? themeColors.background : themeColors.textSecondary,
                fontSize: '0.75rem',
                fontWeight: isSelected ? 600 : 400,
                transition: 'all 0.2s ease',
              }}
            />
          );
        }
        return (
          <Typography 
            variant="body2" 
            sx={{ 
              color: isSelected ? themeColors.primary : themeColors.text,
              fontWeight: isSelected ? 600 : 400,
              transition: 'all 0.2s ease',
            }}
          >
            {formatFileSize((item as S3Object).size)}
          </Typography>
        );
      },
    },
    {
      field: 'lastModified',
      headerName: 'Modified',
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const item = params.row as S3Item;
        const isSelected = selectionModel.includes(item.key);
        
        if (item.type === 'folder') {
          return null;
        }
        return (
          <Typography 
            variant="body2" 
            sx={{ 
              color: isSelected ? themeColors.primary : themeColors.textSecondary,
              fontWeight: isSelected ? 600 : 400,
              transition: 'all 0.2s ease',
            }}
          >
            {formatDate((item as S3Object).lastModified)}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const item = params.row as S3Item;
        const isSelected = selectionModel.includes(item.key);
        
        if (item.type === 'folder') {
          return (
            <Tooltip title="Open folder">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateTo(item.key);
                }}
                sx={{ 
                  color: isSelected ? themeColors.primary : themeColors.primary,
                  opacity: isSelected ? 1 : 0.7,
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                <FolderOpenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        }

        return (
          <Tooltip title="Download file">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(item as S3Object);
              }}
              sx={{ 
                color: isSelected ? themeColors.primary : themeColors.primary,
                opacity: isSelected ? 1 : 0.7,
                '&:hover': {
                  opacity: 1,
                },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
  ];

  /**
   * Prepare rows for DataGrid with stable unique identifiers.
   * 
   * Uses useMemo to prevent unnecessary recomputation and ensure stable references.
   * Each row uses item.key (S3 object key/path) as the unique ID, which is:
   * - Guaranteed unique within the S3 bucket
   * - Stable across filter changes and re-renders
   * - Used by DataGrid as the React key for DOM reconciliation
   * 
   * Development-time duplicate key detection helps catch data issues early.
   */
  const rows: S3ItemWithId[] = useMemo(() => {
    const mappedRows = items.map((item) => ({
      ...item,
      id: item.key, // DataGrid requires unique 'id' field - uses this as React key
    }));

    // Development-time check for duplicate keys
    if (process.env.NODE_ENV === 'development' && mappedRows.length > 0) {
      const keySet = new Set<string>();
      const duplicates: string[] = [];
      
      for (const row of mappedRows) {
        if (keySet.has(row.id)) {
          duplicates.push(row.id);
        } else {
          keySet.add(row.id);
        }
      }
      
      if (duplicates.length > 0) {
        console.warn(
          '[S3FileTable] Duplicate keys detected in filtered reports. This may cause rendering issues.',
          { duplicateKeys: duplicates, totalItems: mappedRows.length }
        );
      }
    }

    return mappedRows;
  }, [items]); // Recompute only when items change


  if (!isAuthenticated) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Authentication required to access files
      </Alert>
    );
  }

  // Priority-based conditional rendering for controlled mode
  // 1. Loading state (highest priority)
  if (loading && items.length === 0) {
    return (
      <Box sx={{ height: 400 }}>
        <TableSkeleton rows={8} columns={4} />
      </Box>
    );
  }

  // 2. Error state
  if (currentError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {currentError}
      </Alert>
    );
  }

  // 3. Empty state (no items found)
  if (items.length === 0 && !loading) {
    return (
      <Box sx={{ mt: 2, textAlign: 'center', py: 6 }}>
        <Typography variant="h6" sx={{ color: themeColors.textSecondary, mb: 2 }}>
          No reports found
        </Typography>
        <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
          {isControlledMode 
            ? 'No reports match the selected filters. Try adjusting your filter criteria.'
            : 'This folder is empty or no files are available at this path.'}
        </Typography>
      </Box>
    );
  }

  // 4. Default: Render file list

  return (
    <Box sx={{ width: '100%' }}>
      {/* Data grid wrapper with custom pagination controls */}
      <Box
        sx={{
          border: `1px solid ${themeColors.accent}`,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Selection Controls Header */}
        {items.length > 0 && (
          <S3SelectionControls
            items={items}
            selectedItems={state.selection.selectedItems}
            onSelectionChange={selectItems}
            disabled={loading}
          />
        )}

        {/* Data grid */}
        <Box sx={{ height: 500, width: '100%', overflowY: 'auto' }} onScroll={isControlledMode ? undefined : handleScroll}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading && items.length > 0}
            // TODO: FEATURE-CHECKBOX-SELECTION [REMOVED]
            // Description: DataGrid checkboxSelection prop and selection model
            // Restore: Uncomment lines below to restore checkbox selection
            // Ref: docs/feature-plans/checkbox-selection-removal-plan.md
            // checkboxSelection
            disableRowSelectionOnClick
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            // rowSelectionModel={selectionModel}
            // onRowSelectionModelChange={handleSelectionChange}
            onRowDoubleClick={handleRowDoubleClick}
            paginationMode="server"
            rowCount={isControlledMode && paginationMetadata ? paginationMetadata.totalCount : -1}
            hideFooter={true} // Always hide DataGrid footer, use custom pagination controls
            sx={{
              bgcolor: 'transparent',
              border: 'none',
              '& .MuiDataGrid-cell': {
                color: themeColors.text,
                borderBottom: `1px solid ${themeColors.accent}`,
                transition: 'background-color 0.2s ease',
              },
              '& .MuiDataGrid-columnHeader': {
                bgcolor: themeColors.accent,
                color: themeColors.text,
                borderBottom: `2px solid ${themeColors.primary}`,
              },
              '& .MuiDataGrid-footerContainer': {
                bgcolor: themeColors.accent,
                borderTop: `1px solid ${themeColors.primary}`,
                color: themeColors.text,
              },
              '& .MuiDataGrid-row': {
                transition: 'background-color 0.2s ease',
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: `${themeColors.accent}40`,
              },
              '& .MuiDataGrid-row.Mui-selected': {
                bgcolor: `${themeColors.primary}15 !important`,
                '&:hover': {
                  bgcolor: `${themeColors.primary}25 !important`,
                },
              },
              // TODO: FEATURE-CHECKBOX-SELECTION [REMOVED]
              // Description: Checkbox styling
              // Restore: Uncomment lines below to restore checkbox styling
              // Ref: docs/feature-plans/checkbox-selection-removal-plan.md
              // '& .MuiCheckbox-root': {
              //   color: themeColors.primary,
              // },
              // '& .MuiCheckbox-root.Mui-checked': {
              //   color: themeColors.primary,
              // },
              '& .MuiDataGrid-cell:focus': {
                outline: `1px solid ${themeColors.primary}`,
              },
            }}
          />
        </Box>

        {/* Custom pagination controls (only in controlled mode) */}
        {isControlledMode && (
          <S3PaginationControls
            paginationMetadata={paginationMetadata}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            disabled={loading}
          />
        )}
        
        {/* Load More button for standalone mode */}
        {!isControlledMode && hasNextPage && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: `1px solid ${themeColors.accent}` }}>
            <Button
              variant="contained"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              sx={{
                bgcolor: themeColors.primary,
                color: '#fff',
                '&:hover': {
                  bgcolor: `${themeColors.primary}cc`,
                },
              }}
            >
              {isFetchingNextPage ? 'Loading...' : `Load More (${items.length} of many items shown)`}
            </Button>
          </Box>
        )}
      </Box>

      {/* Download Manager */}
      <S3DownloadManager
        progress={downloadProgress}
        status={downloadStatus}
        message={downloadMessage}
        onRetry={handleRetry}
      />

      {/* Upload Progress Panel - Shows only when there are active/queued upload jobs */}
      {uploadState.jobs.size > 0 && (
        <Box sx={{ mt: 2 }}>
          <S3UploadProgress
            uploadJobs={Array.from(uploadState.jobs.values())}
            show={uploadState.jobs.size > 0}
            title="Upload Progress"
          />
        </Box>
      )}

      {/* Summary info */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={2}>
          <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
            {items.filter(item => item.type === 'folder').length} folders, {items.filter(item => item.type === 'file').length} files
          </Typography>
          {state.selection.selectionCount > 0 && (
            <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
              {state.selection.selectionCount} selected
            </Typography>
          )}
        </Stack>
        
        {!isControlledMode && hasNextPage && (
          <Typography variant="body2" sx={{ color: themeColors.textSecondary }}>
            More items available
          </Typography>
        )}
      </Box>

      {/* Toolbar with Upload and Download Actions */}
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {/* Upload Files Button */}
        <S3UploadButton
          currentPath={currentPath}
          authenticated={isAuthenticated}
          writePermission={isAuthenticated}
          mode="files"
          label="Upload Files"
          variant="contained"
          size="medium"
        />
        
        {/* Upload Folder Button */}
        <S3UploadButton
          currentPath={currentPath}
          authenticated={isAuthenticated}
          writePermission={isAuthenticated}
          mode="folder"
          label="Upload Folder"
          variant="outlined"
          size="medium"
        />
        
        {/* TODO: FEATURE-CHECKBOX-SELECTION [REMOVED] */}
        {/* Description: Bulk Download Button - hidden until checkbox selection restored */}
        {/* Restore: Uncomment the code block below to restore bulk download button */}
        {/* Ref: docs/feature-plans/checkbox-selection-removal-plan.md */}
        {/* Bulk Download Button */}
        {/* {state.selection.selectedFiles.length > 0 && (
          <Button
            variant="contained"
            onClick={handleBulkDownload}
            disabled={downloadStatus !== 'idle'}
            startIcon={<DownloadIcon />}
            sx={{
              bgcolor: themeColors.primary,
              color: themeColors.background,
              '&:hover': {
                bgcolor: themeColors.primary,
                opacity: 0.8,
              },
              '&:disabled': {
                bgcolor: themeColors.accent,
                color: themeColors.textSecondary,
              },
            }}
          >
            Download {state.selection.selectedFiles.length} File{state.selection.selectedFiles.length > 1 ? 's' : ''} as ZIP
          </Button>
        )} */}
      </Box>
    </Box>
  );
};

export default S3FileTable;
